import { useRef, useState } from 'react';
import {
  Popover,
  ListBox,
  ListBoxItem,
  type Selection,
} from 'react-aria-components';
import { Check, ChevronDown } from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { Chip } from './Chip';
import { typography } from './Text';
import type { SelectOption } from './Select';
import { useT } from '../../modules/i18n';

export interface MultiSelectProps {
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  fullWidth?: boolean;
  className?: string;
}

/**
 * Multi-select chip input — selected values render as removable Chips inside a
 * bordered input box; a dropdown ListBox (multiple selection) adds/removes.
 * Built from the Chip primitive + foundation tokens.
 */
export const MultiSelect = ({
  options,
  value,
  onChange,
  label,
  placeholder,
  isRequired,
  fullWidth,
  className,
}: MultiSelectProps) => {
  const t = useT();
  const resolvedPlaceholder = placeholder ?? t('ui.multiSelect.placeholder');
  const triggerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const selected = options.filter(o => value.includes(o.value));

  const handleSelectionChange = (keys: Selection) => {
    onChange(
      keys === 'all' ? options.map(o => o.value) : [...keys].map(String),
    );
  };

  return (
    <div
      className={cx('flex flex-col gap-1.5', fullWidth && 'w-full', className)}
    >
      {label && (
        <span className={cx(typography.label, 'text-fg-secondary')}>
          {label}
          {isRequired && <span className="ml-0.5 text-brand-text">*</span>}
        </span>
      )}
      <div
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(o => !o);
          }
        }}
        className={cx(
          'flex min-h-10 w-full cursor-pointer flex-wrap items-center gap-1.5 rounded-lg border border-strong bg-surface px-2 py-1.5 shadow-xs outline-none transition',
          'hover:border-brand-300 focus-visible:border-brand-300 focus-visible:ring-4 focus-visible:ring-[rgb(var(--c-ring)/0.24)]',
          open && 'border-brand-300 ring-4 ring-[rgb(var(--c-ring)/0.24)]',
        )}
      >
        {selected.length === 0 ? (
          <span className={cx(typography.body, 'px-1 text-fg-muted')}>
            {resolvedPlaceholder}
          </span>
        ) : (
          // One compact pill instead of a chip per value, so the control stays
          // short however many are picked. × clears all; remove individual
          // values by re-opening and unchecking.
          // Presentational wrapper — the interactive element is the Chip itself.
          <span
            role="presentation"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
          >
            <Chip
              label={
                selected.length === 1
                  ? selected[0].label
                  : t('ui.multiSelect.selectedCount', {
                      count: selected.length,
                    })
              }
              removeLabel={t('ui.multiSelect.clearSelection')}
              onRemove={() => onChange([])}
            />
          </span>
        )}
        <Icon
          as={ChevronDown}
          size="sm"
          className="ml-auto shrink-0 text-fg-muted"
        />
      </div>
      <Popover
        triggerRef={triggerRef}
        isOpen={open}
        placement="bottom start"
        onOpenChange={setOpen}
        // The trigger is a plain element (not a RAC trigger), so without this
        // the click that opens the popover is itself seen as an outside
        // interaction and closes it again — the dropdown flickers and feels
        // unclickable. Treat clicks within the trigger as inside.
        shouldCloseOnInteractOutside={el => !triggerRef.current?.contains(el)}
        className="min-w-56 w-(--trigger-width) rounded-lg border border-default bg-surface p-1 shadow-lg outline-none entering:animate-in entering:fade-in-0"
      >
        <ListBox
          selectionMode="multiple"
          selectedKeys={new Set(value)}
          onSelectionChange={handleSelectionChange}
          items={options}
          className="max-h-72 overflow-auto outline-none"
        >
          {(option: SelectOption) => (
            <ListBoxItem
              id={option.value}
              textValue={option.label}
              className="flex cursor-pointer select-none items-center justify-between gap-3 rounded-md px-2.5 py-2 text-sm text-fg-secondary outline-none data-[focused]:bg-muted-strong data-[selected]:text-fg data-[selected]:font-medium"
            >
              {({ isSelected }) => (
                <>
                  <span className="flex items-center gap-2">
                    {option.icon}
                    {option.label}
                  </span>
                  {isSelected && (
                    <Icon
                      as={Check}
                      size="sm"
                      strokeWidth={2.5}
                      className="text-brand-text"
                    />
                  )}
                </>
              )}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </div>
  );
};
